import { useQuery } from '@tanstack/react-query';

import { isPrometheusConfigured } from '@/shared/config/prometheus-config';
import { DEFAULT_QUERY_OPTIONS, useQueryWithBoundary } from '@/shared/lib/query';
import { monitoringKeys } from '@/shared/lib/query/queryKeys';

import {
  getInstantValue,
  getRangeValues,
  getRangeValuesByPod,
  getValuesByPod,
  PROMETHEUS_QUERIES,
  QUERY_INTERVALS,
  RANGE_SECONDS,
  REFETCH_INTERVAL,
  STALE_TIME,
} from '@/features/monitoring/api';
import type { ResourceUsageData } from '@/features/monitoring/api';

import { assignOrderedPodSeriesColors } from './lib/assignOrderedPodSeriesColors';

export const useCpuUsage = () => {
  const enabled = isPrometheusConfigured();

  const summaryQuery = useQueryWithBoundary<Pick<ResourceUsageData, 'current' | 'total' | 'percentage' | 'unit'>>({
    queryKey: monitoringKeys.cpu(),
    queryFn: async () => {
      const [current, request] = await Promise.all([
        getInstantValue(PROMETHEUS_QUERIES.CPU.TOTAL),
        getInstantValue(PROMETHEUS_QUERIES.CPU.REQUEST),
      ]);

      const total = request || 1;
      const percentage = (current / total) * 100;

      return {
        current,
        total,
        percentage,
        unit: 'cores',
      };
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    ...DEFAULT_QUERY_OPTIONS,
  });

  const detailQuery = useQuery<Pick<ResourceUsageData, 'byPod' | 'history' | 'podHistory'>>({
    queryKey: [...monitoringKeys.cpuPrefix(), 'detail'],
    queryFn: async () => {
      const now = Math.floor(Date.now() / 1000);

      const [byPod, history, podHistoryRaw] = await Promise.all([
        getValuesByPod(PROMETHEUS_QUERIES.CPU.BY_POD),
        getRangeValues(PROMETHEUS_QUERIES.CPU.HISTORY, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
        getRangeValuesByPod(PROMETHEUS_QUERIES.CPU.BY_POD, RANGE_SECONDS, QUERY_INTERVALS.STEP_1M, now),
      ]);

      const podHistory = assignOrderedPodSeriesColors(podHistoryRaw);

      return {
        byPod: byPod.sort((a, b) => b.value - a.value),
        podHistory,
        history,
      };
    },
    enabled,
    refetchInterval: REFETCH_INTERVAL,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const data: ResourceUsageData | undefined = summaryQuery.data
    ? {
        ...summaryQuery.data,
        byPod: detailQuery.data?.byPod ?? [],
        history: detailQuery.data?.history ?? [],
        podHistory: detailQuery.data?.podHistory ?? [],
      }
    : undefined;

  return {
    ...summaryQuery,
    data,
    isFetching: summaryQuery.isFetching || detailQuery.isFetching,
    isLoading: summaryQuery.isLoading,
  };
};
